// Cloud-shadow density sampling — injected into the terrain materials so the
// volumetric deck casts a soft, drifting shadow onto the ground.
//
// This is a trimmed copy of the beauty-pass density field
// (cloud_raymarch_frag.glsl): it reads the SAME packed noise texture and uses
// the SAME drift / coverage / height shaping, so the shadow pattern lines up
// with the clouds overhead. It runs at fewer octaves and fewer march steps —
// shadows are low-frequency, so the fine wisps and a dense march are wasted
// here. Kept in sync by hand with the beauty pass; if the cloud SHAPE changes
// there (fbm weights, coverage/height shaping), mirror it here.
//
// CLOUD_SHADOW_OCTAVES / CLOUD_SHADOW_STEPS are baked as #defines by the host
// material (from SimConfig.CLOUDS.shadow) ahead of this block.

#ifndef CLOUD_SHADOW_OCTAVES
#define CLOUD_SHADOW_OCTAVES 4
#endif
#ifndef CLOUD_SHADOW_STEPS
#define CLOUD_SHADOW_STEPS 2
#endif

varying vec3 vCloudShadowWorldPos;

uniform float     uCloudShadowStrength;   // 0 disables the cast (no clouds / no sun)
uniform sampler2D uCloudShadowNoise;      // shared with CloudLayer (packed value noise)
uniform float     uCloudShadowCoverage;
uniform float     uCloudShadowDensity;    // extinction multiplier (matches CLOUDS density)
uniform float     uCloudShadowEdge;       // coverage-threshold feather width
uniform float     uCloudShadowNoiseScale; // metres per base FBM octave wrap
uniform vec3      uCloudShadowWindDir;    // normalized drift direction
uniform float     uCloudShadowWindSpeed;  // m/s
uniform float     uCloudShadowTime;       // seconds (shared cloud clock)
uniform float     uCloudShadowBase;       // slab base, metres ASL
uniform float     uCloudShadowTop;        // slab top, metres ASL
uniform vec3      uCloudShadowSunDir;     // toward the sun (world, normalized)

// 3D value noise from the packed 2D texture (IQ trick) — identical to the
// beauty pass so the fields match.
float csNoise(vec3 x) {
  vec3 p = floor(x);
  vec3 f = fract(x);
  f = f * f * (3.0 - 2.0 * f);
  vec2 uv = (p.xy + vec2(37.0, 239.0) * p.z) + f.xy;
  vec2 tex = texture2D(uCloudShadowNoise, (uv + 0.5) / 256.0).yx;
  return mix(tex.x, tex.y, f.z) * 2.0 - 1.0;
}

float csFbm(vec3 p) {
  float f = 0.0;
  float scale = 0.5;
  float factor = 2.02;
  for (int i = 0; i < CLOUD_SHADOW_OCTAVES; i++) {
    f += scale * csNoise(p);
    p *= factor;
    factor += 0.21;
    scale *= 0.5;
  }
  return f;
}

// Cloud density [0,1] at a world point — mirrors cloud_raymarch_frag's
// sampleDensity (soft base feather + height-eroded coverage threshold).
float csDensity(vec3 p) {
  float t = (p.y - uCloudShadowBase) / max(uCloudShadowTop - uCloudShadowBase, 1.0);
  float baseFade = smoothstep(0.0, 0.12, t);
  if (baseFade <= 0.0) return 0.0;

  vec3 drift = uCloudShadowWindDir * (uCloudShadowTime * uCloudShadowWindSpeed / uCloudShadowNoiseScale);
  float shape = csFbm(p / uCloudShadowNoiseScale + drift) * 0.5 + 0.5;

  float lo = 1.0 - uCloudShadowCoverage;
  lo = mix(lo, 1.0, smoothstep(0.25, 1.0, clamp(t, 0.0, 1.0)));
  float d = smoothstep(lo, min(lo + uCloudShadowEdge, 1.0), shape);
  return d * baseFade;
}

// Fraction of sunlight reaching worldPos through the deck (1 = full sun, down
// to 1 - uCloudShadowStrength in the deepest umbra).
//
// The shadow follows the density field's coverage SHAPE — averaged over a few
// samples along the sun ray through the slab — rather than the true optical
// depth. That's deliberate: the deck is near-opaque, so a real transmittance
// integral would saturate to black the instant the ray grazes any density,
// collapsing the penumbra. Using the coverage shape keeps the broad, soft
// edges the density feather (uCloudShadowEdge) already provides. The overall
// darkness is then scaled by the deck's column opacity, so a thin/wispy deck
// (low density × thin slab) casts a fainter shadow than a thick solid one.
float cloudShadowFactor(vec3 worldPos) {
  if (uCloudShadowStrength <= 0.0) return 1.0;
  vec3 s = uCloudShadowSunDir;
  // Near/below the horizon the slant path through the slab explodes and the
  // deck no longer reads as an overhead caster — skip the cast, fading it out
  // as the sun approaches the horizon so it doesn't pop off.
  if (s.y < 0.05) return 1.0;
  float horizonFade = smoothstep(0.05, 0.2, s.y);

  float tA = (uCloudShadowBase - worldPos.y) / s.y;
  float tB = (uCloudShadowTop  - worldPos.y) / s.y;
  float t0 = max(min(tA, tB), 0.0);
  float t1 = max(tA, tB);
  if (t1 <= t0) return 1.0;

  float dt = (t1 - t0) / float(CLOUD_SHADOW_STEPS);
  float dens = 0.0;
  for (int i = 0; i < CLOUD_SHADOW_STEPS; i++) {
    vec3 p = worldPos + s * (t0 + (float(i) + 0.5) * dt);
    dens += csDensity(p);
  }
  dens /= float(CLOUD_SHADOW_STEPS);   // mean coverage 0..1 (soft-edged)

  // Opacity of a full-density column of this deck (matches the beauty pass's
  // 1 - exp(-density·thickness)), so thin decks shadow lightly, thick ones
  // fully. Constant across the frame; the per-pixel variation is all in dens.
  float columnOpacity = 1.0 - exp(-uCloudShadowDensity * max(uCloudShadowTop - uCloudShadowBase, 1.0));
  float shadowAmt = clamp(dens, 0.0, 1.0) * columnOpacity;

  return 1.0 - shadowAmt * uCloudShadowStrength * horizonFade;
}
